import { Droplets } from "lucide-react";
import { Camera as CamIcon, Image as ImgIcon, X as XIcon } from "lucide-react";
import ProofButton from "./ProofButton";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { PageHeader, EmptyState } from "./OwnerPages";
import { useAuth } from "@/context/AuthContext";
import LocationDocumentsBlock from "./LocationDocumentsBlock";
import LinkInvoiceButton from "./LinkInvoiceButton";
import { DayPlanCard, DayReportScreen, SaveLocationButton } from "./DayPlanPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Fingerprint, MapPin, Play, Square, ShoppingCart, Users, Handshake, Store, Plus, Minus, Navigation, Search, LayoutGrid, List as ListIcon, ClipboardList, Edit, Ban, Wallet, IndianRupee, Filter as FilterIcon, RefreshCw, ChevronRight, Package } from "lucide-react";

// ============================================================================
// Salesperson Dashboard with punch in/out — Phase 1: all cards clickable
// ============================================================================
export function SalespersonDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [punch, setPunch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [todayCollection, setTodayCollection] = useState(null);
  const [canIn, setCanIn] = useState(true);
  const nav = useNavigate();
  const load = () => dms.salespersonDashboard().then(d => {
    setKpis(d.kpis);
    // same source as the Attendance page, so both screens always agree
    dms.punchToday()
      .then(t => { setPunch(t.punch || null); setCanIn(!!t.can_punch_in); })
      .catch(() => { setPunch(d.today_punch); });
  });
  useEffect(() => { load(); }, []);
  useEffect(() => { dms.salespersonCollections({}).then(d => setTodayCollection(d.total)).catch(() => {}); }, []);
  const [reportOpen, setReportOpen] = useState(false);
  const doPunch = async (kind) => {
    if (kind === "out") { setReportOpen(true); return; }
    setBusy(true);
    let coords = { lat: null, lng: null };
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
      coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch { /* fallback */ }
    try {
      await dms.punchIn(coords);
      toast.success("Punched in");
      window.dispatchEvent(new Event("dms-punch-changed"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const [saleL, setSaleL] = useState(null);
  useEffect(() => { dms.spSaleLiters({}).then(d => setSaleL(d.total_liters ?? 0)).catch(() => {}); }, []);
  const isPunchedIn = punch && !punch.out_at;
  const cards = [
    { label: "Assigned Distributors", value: kpis?.assigned_distributors ?? "—", icon: Handshake, color: "teal",   to: "/dms/salesperson/distributors" },
    { label: "Assigned Retailers",   value: kpis?.assigned_retailers ?? "—",   icon: Store,     color: "indigo", to: "/dms/salesperson/retailers" },
    { label: "Orders Today",         value: kpis?.orders_today ?? "—",         icon: ShoppingCart, color: "amber", to: "/dms/salesperson/orders" },
    { label: "Sale in Liters", value: saleL != null ? `${Number(saleL).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L` : "—", icon: Droplets, color: "teal", to: "/dms/salesperson/sale-liters" },
    { label: "Today's Collection",   value: todayCollection != null ? inr(todayCollection) : "—", icon: IndianRupee, color: "emerald", to: "/dms/salesperson/collections" },
    { label: "Outstanding",          value: kpis ? inr(kpis.outstanding || 0) : "—", icon: IndianRupee, color: "rose", to: "/dms/salesperson/outstanding" },
  ];
  const colorMap = { teal: "bg-[#faf6e6] text-[#a67c00]", indigo: "bg-indigo-50 text-indigo-700", amber: "bg-amber-50 text-amber-700", emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700" };
  return (
    <div>
      <PageHeader title="Salesperson Dashboard" subtitle="Punch in, visit retailers, capture orders" />
      <Card className={`p-6 mb-4 ${isPunchedIn ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-600 flex items-center gap-1"><Fingerprint size={12} /> Today's attendance</div>
            {isPunchedIn ? (
              <>
                <div className="font-bold text-emerald-800 mt-1">Punched in at {niceDate(punch.in_at)}</div>
                {punch.gps_in?.lat && <div className="text-xs text-emerald-700 flex items-center gap-1 mt-1"><MapPin size={11} /> {punch.gps_in.lat.toFixed(4)}, {punch.gps_in.lng.toFixed(4)}</div>}
              </>
            ) : punch?.out_at ? (
              <div className="font-medium text-slate-700 mt-1">Punched out at {niceDate(punch.out_at)}</div>
            ) : (
              <div className="font-medium text-slate-700 mt-1">Not punched in yet</div>
            )}
          </div>
          <div className="flex gap-2">
            {!isPunchedIn && canIn && <Button onClick={() => doPunch("in")} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="punch-in-btn"><Play size={14} className="mr-1" /> Punch In</Button>}
            {isPunchedIn && <Button onClick={() => doPunch("out")} disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid="punch-out-btn"><Square size={14} className="mr-1" /> Punch Out</Button>}
          </div>
        </div>
      </Card>
      <DayReportScreen open={reportOpen} punchId={punch?.id} onClose={() => setReportOpen(false)}
        onDone={() => { setReportOpen(false); load(); }} />
      <DayPlanCard />

      {/* KPI cards — clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {cards.map(c => (
          <Card key={c.label} className="p-4 cursor-pointer hover:shadow-md transition"
                onClick={() => nav(c.to)} data-testid={`sp-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${colorMap[c.color]}`}><c.icon size={18} /></div>
            <div className="mt-3 text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
            <div className="mt-1 text-xl font-bold text-slate-900">{c.value}</div>
          </Card>
        ))}
      </div>

      {/* Quick actions — Phase 1: added My Orders + Collect Payment */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/salesperson/distributors")}><Handshake size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">My Distributors</div><div className="text-xs text-slate-500">View stock & details</div></Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/salesperson/retailers")}><Store size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">My Retailers</div><div className="text-xs text-slate-500">Visit, onboard, place orders</div></Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/salesperson/orders")} data-testid="sp-my-orders-tile"><ClipboardList size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">My Orders</div><div className="text-xs text-slate-500">Track / edit / cancel</div></Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/salesperson/collect")} data-testid="sp-collect-tile"><Wallet size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">Receive Payment</div><div className="text-xs text-slate-500">Cash / UPI / Cheque</div></Card>
      </div>
    </div>
  );
}

export function SpDistributorsPage() {
  const [list, setList] = useState([]);
  const [stockFor, setStockFor] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  useEffect(() => { dms.listDistributors().then(d => setList(d.data)); }, []);

  // Point 72: salesperson can view assigned distributor's stock
  const openStock = async (d) => {
    setStockFor(d);
    setStockLoading(true);
    try {
      const res = await dms.spDistributorStock(d.id);
      setStockData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load stock");
      setStockData(null);
    }
    setStockLoading(false);
  };

  return (
    <div>
      <PageHeader title="My Assigned Distributors" subtitle="Distributors your team leader has assigned to you" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(d => (
          <Card key={d.id} className="p-4 cursor-pointer hover:bg-slate-50" onClick={() => openStock(d)} data-testid={`sp-dist-card-${d.id}`}>
            <div className="font-semibold">{d.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{d.email}</div>
            <div className="text-xs text-slate-500">{d.phone}</div>
            <div className="text-xs text-slate-500 mt-1">Region: {d.region || "—"}</div>
            <div className="text-[11px] text-[#a67c00] font-medium mt-2">View Stock →</div>
          </Card>
        ))}
        {list.length === 0 && <Card className="p-8 col-span-full text-center text-sm text-slate-500">No distributors assigned yet</Card>}
      </div>

      <Dialog open={!!stockFor} onOpenChange={o => { if (!o) { setStockFor(null); setStockData(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Stock — {stockFor?.name}</DialogTitle></DialogHeader>
          {stockLoading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!stockLoading && stockData && (
            <>
              <div className="text-sm text-slate-500 mb-3">Total stock value {inr(stockData.total_value || 0)}</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-500 text-xs uppercase">
                    <th className="p-2">Product</th>
                    <th className="p-2 text-right">Qty (Boxes)</th>
                    <th className="p-2 text-right">No. of Pieces</th>
                    <th className="p-2 text-right">Sales Price</th>
                    <th className="p-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(stockData.data || []).map((it) => (
                    <tr key={it.product_id} className="border-t border-slate-100">
                      <td className="p-2">
                        <div className="font-medium text-slate-900">{it.product_name}</div>
                        {it.sku_code && <div className="font-mono text-[10px] text-slate-500">{it.sku_code}</div>}
                      </td>
                      <td className="p-2 text-right">{it.qty_boxes}</td>
                      <td className="p-2 text-right">{it.no_of_pieces}</td>
                      <td className="p-2 text-right">{inr(it.sales_price)}</td>
                      <td className="p-2 text-right font-semibold">{inr(it.line_value)}</td>
                    </tr>
                  ))}
                  {(stockData.data || []).length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-400">No stock available</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// SP: My Retailers — Phase 1: Search + List/Grid toggle
// ============================================================================
export function SpRetailersPage() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid"); // 'grid' | 'list'
  const nav = useNavigate();
  const loadRetailers = () => dms.listRetailers({ lite: 1 }).then(d => setList(d.data || []));
  useEffect(() => { loadRetailers(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(r =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.phone || "").toLowerCase().includes(needle) ||
      (r.address || "").toLowerCase().includes(needle) ||
      (r.region || "").toLowerCase().includes(needle),
    );
  }, [list, q]);

  return (
    <div>
      <PageHeader title="My Retailers" subtitle={`${filtered.length} of ${list.length} retailers`}
        action={<Button onClick={() => nav("/dms/salesperson/new-retailer")} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="sp-new-retailer"><Plus size={16} className="mr-1" /> Onboard Retailer</Button>} />

      {/* Search + View toggle */}
      <div className="flex flex-col md:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, phone, address or region…" className="pl-9" data-testid="sp-retailer-search" />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 self-start">
          <button onClick={() => setView("grid")} data-testid="sp-ret-view-grid"
                  className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1 ${view === "grid" ? "bg-[#faf0cf] text-[#8a6600] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
            <LayoutGrid size={13} /> Grid
          </button>
          <button onClick={() => setView("list")} data-testid="sp-ret-view-list"
                  className={`px-3 py-1.5 text-xs rounded-md flex items-center gap-1 ${view === "list" ? "bg-[#faf0cf] text-[#8a6600] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
            <ListIcon size={13} /> List
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">{list.length === 0 ? "No retailers yet" : "No retailers match your search"}</Card>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(r => (
            <Card key={r.id} className="p-4">
              <div className="font-semibold">{r.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{r.phone}</div>
              <div className="text-xs text-slate-500 truncate">{r.address}</div>
              {r.gps_lat && <a href={`https://maps.google.com/?q=${r.gps_lat},${r.gps_lng}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#a67c00] hover:underline flex items-center gap-1 mt-1"><MapPin size={11} /> View on Maps</a>}
              <div className="mt-2"><SaveLocationButton retailer={r} onSaved={loadRetailers} /></div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => nav(`/dms/salesperson/new-order?retailer_id=${r.id}`)} data-testid={`sp-order-for-${r.id}`}><ShoppingCart size={12} className="mr-1" /> Place Order</Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/dms/salesperson/edit-retailer/${r.id}`)} data-testid={`sp-edit-${r.id}`}><Edit size={12} /></Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-slate-600">{r.phone}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-xs truncate">{r.address}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.region || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <SaveLocationButton retailer={r} onSaved={loadRetailers} />
                      <Button size="sm" variant="outline" onClick={() => nav(`/dms/salesperson/new-order?retailer_id=${r.id}`)}>
                        <ShoppingCart size={12} className="mr-1" /> Order
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => nav(`/dms/salesperson/edit-retailer/${r.id}`)}>
                        <Edit size={12} className="mr-1" /> Edit
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

export function SpNewRetailerPage() {
  const [form, setForm] = useState({ name: "", phone: "", address: "", region: "", email: "", password: "Demo@2026", gstin: "", shop_license: "", distributor_id: "", gps_lat: "", gps_lng: "", location_link: "", documents: [], retailer_type: "retailer" });
  const [dists, setDists] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  useEffect(() => { dms.listDistributors().then(d => { setDists(d.data); if (d.data[0]) setForm(f => ({ ...f, distributor_id: d.data[0].id })); }); }, []);
  const submit = async () => {
    // If a login (email) is being created, require full details + KYC + document.
    if (String(form.email || "").trim()) {
      const req = { region: "Region", gstin: "GSTIN", shop_license: "Shop License", password: "Password" };
      const missing = Object.entries(req)
        .filter(([k]) => !String(form[k] || "").trim())
        .map(([, label]) => label);
      if (!(form.documents || []).length) missing.push("at least one Document");
      if (missing.length) {
        toast.error("To create a retailer login, complete: " + missing.join(", "));
        return;
      }
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        gps_lat: form.gps_lat === "" ? null : Number(form.gps_lat),
        gps_lng: form.gps_lng === "" ? null : Number(form.gps_lng),
      };
      const r = await dms.createRetailer(body);
      toast.success(`Retailer ${r.name} onboarded`);
      nav(`/dms/salesperson/new-order?retailer_id=${r.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="Onboard New Retailer" subtitle="Basic info + location + documents" back="/dms/salesperson/retailers" />
      <Card className="p-6 space-y-4">
        <div><Label>Assign to Distributor *</Label>
          <select value={form.distributor_id} onChange={e => setForm({ ...form, distributor_id: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200">
            {dists.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Shop Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="sp-ret-name" /></div>
          <div><Label>Phone *</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div><Label>Address *</Label><Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Region</Label><Input value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} /></div>
          <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit || ""} onChange={e => setForm({ ...form, credit_limit: Number(e.target.value) })} /></div>
        </div>
        <div><Label>Retailer Type *</Label>
          <select className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200" value={form.retailer_type || "retailer"} onChange={e => setForm({ ...form, retailer_type: e.target.value })} data-testid="sp-ret-type">
            <option value="retailer">Retailer</option>
            <option value="wholeseller">Wholeseller</option>
            <option value="workshop">Workshop</option>
            <option value="mechanic">Mechanic</option>
          </select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Login Email (optional)</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Password</Label><Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>GSTIN (optional)</Label><Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
          <div><Label>Shop License (optional)</Label><Input value={form.shop_license} onChange={e => setForm({ ...form, shop_license: e.target.value })} /></div>
        </div>
        <LocationDocumentsBlock
          lat={form.gps_lat}
          lng={form.gps_lng}
          locationLink={form.location_link}
          onLat={(v) => setForm(f => ({ ...f, gps_lat: v }))}
          onLng={(v) => setForm(f => ({ ...f, gps_lng: v }))}
          onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
          documents={form.documents}
          onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
          helpText="Tap Use my current location for the shop's exact GPS pin."
        />
        <Button onClick={submit} disabled={busy || !form.name || !form.phone || !form.address} className="w-full bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="sp-save-retailer">Onboard & Place First Order</Button>
      </Card>
    </div>
  );
}

// ============================================================================
// SP: Order Placement — Phase 1:
//   - category filter chips
//   - always allow "Nos" (pcs) input regardless of mode
//   - supports edit mode (?edit=OID prefills existing items)
// ============================================================================
export function SpNewOrderPage() {
  const params = new URLSearchParams(window.location.search);
  const rid = params.get("retailer_id");
  const editId = params.get("edit");                         // Phase 1: edit mode
  const [data, setData] = useState({ data: [], mode: "box", pending: [] });
  const [cart, setCart] = useState({});
  const [busy, setBusy] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  const nav = useNavigate();

  // Prefill from existing order if editing
  useEffect(() => {
    if (!editId) return;
    dms.getSecondaryOrder(editId).then(o => {
      setExistingOrder(o);
      const c = {};
      (o.items || []).forEach(it => {
        const _pcs = Number(it.qty_boxes_ordered || 0) * Math.max(Number(it.box_qty) || 1, 1) + Number(it.qty_pcs_ordered || 0);
        c[it.product_id] = (c[it.product_id] || 0) + _pcs;
      });
      setCart(c);
    }).catch(() => toast.error("Could not load order to edit"));
  }, [editId]);

  useEffect(() => {
    const targetRid = rid || existingOrder?.retailer_id;
    if (targetRid) dms.retailerBrowse(targetRid).then(setData);
  }, [rid, existingOrder]);

  const setQty = (pid, delta) => setCart(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + delta) }));
  const setQtyDirect = (pid, val) => setCart(prev => ({ ...prev, [pid]: Math.max(0, Number(val) || 0) }));

  const items = Object.entries(cart).filter(([, q]) => q > 0);

  const pcsRateIncl = (p) => p.selling_price / (p.box_qty || 1); // GST-inclusive per piece

  const total = items.reduce((s, [pid, q]) => {
    const p = data.data.find(x => x.id === pid);
    if (!p) return s;
    return s + q * pcsRateIncl(p);
  }, 0);

  // Group by category with counts & in-cart badges (same pattern as Distributor's Browse & Order)
  const grouped = {};
  data.data.forEach(p => { (grouped[p.category_name || "Uncategorised"] ||= []).push(p); });
  const categoryList = Object.entries(grouped).map(([name, prods]) => {
    const inCart = prods.reduce((n, p) => n + ((cart[p.id] || 0) > 0 ? 1 : 0), 0);
    return { name, count: prods.length, inCart };
  });

  const targetRid = rid || existingOrder?.retailer_id;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        items: items.map(([product_id, q]) => {
          const p = data.data.find(x => x.id === product_id);
          return { product_id, qty_boxes: 0, qty_pcs: Math.round(q) };
        }),
      };
      if (editId) {
        await dms.updateSecondaryOrder(editId, body);
        toast.success("Order updated");
        nav("/dms/salesperson/orders");
      } else {
        body.retailer_id = targetRid;
        const o = await dms.placeSecondaryOrder(body);
        toast.success(`Order ${o.order_no} placed`);
        nav("/dms/salesperson/orders");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (!targetRid) return <div className="p-8 text-center text-slate-500">retailer_id missing</div>;

  return (
    <div className="pb-32">
      <PageHeader
        title={openCategory ? openCategory : (editId ? `Edit Order · ${existingOrder?.order_no || "…"}` : `Order for ${data.retailer?.name || "…"}`)}
        subtitle={openCategory ? "Tap + / − to set quantity per product" : `Mode: ${data.mode === "box_pcs" ? "Box + Nos" : "Box (Nos also enterable)"} • Pick a category to see products`}
        back={editId ? "/dms/salesperson/orders" : "/dms/salesperson/retailers"}
        action={openCategory && (
          <Button variant="outline" onClick={() => setOpenCategory(null)} data-testid="sp-back-to-cats"><ChevronRight className="rotate-180 mr-1" size={16} /> All Categories</Button>
        )}
      />

      {/* STEP 1 — category grid */}
      {!openCategory && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categoryList.length === 0 && <EmptyState icon={Package} title="No products available" description="Ask the owner/distributor to add stock or visibility." />}
          {categoryList.map(c => (
            <button
              key={c.name}
              onClick={() => setOpenCategory(c.name)}
              className="text-left bg-white border border-slate-200 hover:border-[#c9a227] hover:shadow-md transition rounded-2xl p-5 relative"
              data-testid={`sp-cat-tile-${c.name}`}
            >
              <div className="h-10 w-10 rounded-xl bg-[#faf6e6] text-[#a67c00] flex items-center justify-center mb-3"><Package size={20} /></div>
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.count} product{c.count !== 1 ? "s" : ""}</div>
              {c.inCart > 0 && (
                <span className="absolute top-3 right-3 bg-[#c9a227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{c.inCart} in cart</span>
              )}
              <div className="mt-4 text-[#a67c00] text-xs font-medium flex items-center">Open <ChevronRight size={12} className="ml-0.5" /></div>
            </button>
          ))}
        </div>
      )}

      {/* STEP 2 — product list inside category */}
      {openCategory && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(grouped[openCategory] || []).map(p => {
            const q = cart[p.id] || 0;
            const priceChanged = p.previous_selling_price && p.previous_selling_price !== p.selling_price;
            const gstFrac = 1 + (Number(p.gst_pct) || 0) / 100;
            const exGstPricePerNos = (p.selling_price / gstFrac) / (p.box_qty || 1);
            const exGstPrevPricePerNos = p.previous_selling_price ? (p.previous_selling_price / gstFrac) / (p.box_qty || 1) : null;
            return (
              <Card key={p.id} className="p-4 flex flex-col" data-testid={`sp-product-card-${p.id}`}>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-slate-900 leading-tight">{p.name}</div>
                    {priceChanged && <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0">Price ↑</span>}
                  </div>
                  <div className="text-xs font-mono text-slate-500 mb-2">{p.sku_code} • {p.box_qty} pcs/box</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-lg font-bold text-[#a67c00]">{inr(Math.round(exGstPricePerNos))}</span>
                    <span className="text-xs text-slate-500">/ nos (excl. GST)</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">+{p.gst_pct}% GST • Stock: {Number(p.distributor_stock_pcs || 0)} pcs ({Number(p.distributor_stock_boxes || 0)} boxes)</div>
                </div>

                {/* Pieces qty */}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-600">Pieces</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setQty(p.id, -1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center" data-testid={`sp-pcs-minus-${p.id}`}><Minus size={16} /></button>
                    <input
                      type="number" min="0"
                      value={q}
                      onChange={e => setQtyDirect(p.id, e.target.value)}
                      className="w-14 text-center font-semibold border border-slate-200 rounded-lg h-9"
                      data-testid={`sp-pcs-input-${p.id}`}
                    />
                    <button onClick={() => setQty(p.id, 1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center bg-[#faf6e6] text-[#a67c00]" data-testid={`sp-pcs-plus-${p.id}`}><Plus size={16} /></button>
                  </div>
                </div>

                {q > 0 && (
                  <div className="mt-2 text-right text-sm font-semibold text-slate-900">
                    {inr(Math.round(q * pcsRateIncl(p)))}
                    <span className="text-[9px] text-slate-400 font-normal ml-1">incl. GST</span>
                  </div>
                )}
              </Card>
            );
          })}
          {(grouped[openCategory] || []).length === 0 && <Card className="p-8 col-span-full text-center text-sm text-slate-500">No products in this category</Card>}
        </div>
      )}

      <div className="fixed bottom-0 left-0 lg:left-60 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
        <div className="p-4 flex items-center gap-4">
          <div className="flex-1"><div className="text-xs text-slate-500">{items.length} items</div><div className="text-xl font-bold">{inr(total)}</div></div>
          <Button disabled={items.length === 0 || busy} onClick={submit} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white h-11 px-6" data-testid="sp-place-order">
            <ShoppingCart size={16} className="mr-2" /> {editId ? "Update Order" : "Place Order"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SP: My Orders (Phase 1 — BUG FIX + Edit + Cancel)
// ============================================================================
export function SpOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);
  const [reason, setReason] = useState("");
  const [detailOrder, setDetailOrder] = useState(null);
  const nav = useNavigate();

  const load = () => dms.listSecondaryOrders().then(d => setOrders(d.data || [])).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const openDetail = async (o) => {
    try {
      const full = await dms.getSecondaryOrder(o.id);
      setDetailOrder(full);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load order"); }
  };

  const doCancel = async () => {
    if (!cancelFor) return;
    setBusy(true);
    try {
      await dms.cancelSecondaryOrder(cancelFor.id, reason || "Cancelled by salesperson");
      toast.success(`Order ${cancelFor.order_no} cancelled`);
      setCancelFor(null); setReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="My Orders" subtitle="Sales orders you placed for your retailers" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Retailer</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Placed</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No orders yet — place an order from a retailer</TableCell></TableRow>}
            {orders.map(o => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(o)} data-testid={`sp-order-row-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.order_no}</TableCell>
                <TableCell className="font-medium">{o.retailer_name}</TableCell>
                <TableCell className="text-right">{o.items?.length || 0}</TableCell>
                <TableCell className="text-right font-semibold">{inr(o.total)}</TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded-full text-xs border ${statusPill(o.status)}`}>{o.status}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
                <TableCell className="text-right">
                  {o.status === "pending" ? (
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); nav(`/dms/salesperson/new-order?retailer_id=${o.retailer_id}&edit=${o.id}`); }} data-testid={`sp-edit-order-${o.id}`}>
                        <Edit size={12} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={(e) => { e.stopPropagation(); setCancelFor(o); }} data-testid={`sp-cancel-order-${o.id}`}>
                        <Ban size={12} className="mr-1" /> Cancel
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!cancelFor} onOpenChange={o => { if (!o) { setCancelFor(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancel order {cancelFor?.order_no}?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">This will mark the order as cancelled and notify the distributor. This cannot be undone.</div>
          <div className="mt-3">
            <Label>Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Retailer changed mind" data-testid="sp-cancel-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelFor(null)}>Keep Order</Button>
            <Button className="bg-rose-700 hover:bg-rose-800 text-white" disabled={busy} onClick={doCancel} data-testid="sp-cancel-confirm">Confirm Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order detail dialog */}
      <Dialog open={!!detailOrder} onOpenChange={o => { if (!o) setDetailOrder(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order {detailOrder?.order_no}</DialogTitle>
          </DialogHeader>
          {detailOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoBlock label="Retailer" value={detailOrder.retailer?.name || detailOrder.retailer_name} sub={detailOrder.retailer?.phone} />
                <InfoBlock label="Distributor" value={detailOrder.distributor?.name} sub={detailOrder.distributor?.phone} />
                <InfoBlock label="Placed At" value={niceDate(detailOrder.created_at)} />
                <InfoBlock label="Status" value={<span className={`px-2 py-0.5 rounded-full text-xs border ${statusPill(detailOrder.status)}`}>{detailOrder.status}</span>} />
                <InfoBlock label="Total" value={<span className="text-lg font-bold text-[#a67c00]">{inr(detailOrder.total)}</span>} />
              </div>
              {detailOrder.cancel_reason && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800">
                  <b>Cancelled:</b> {detailOrder.cancel_reason}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Items ({detailOrder.items?.length || 0})</div>
                <Card className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead className="text-right">Boxes</TableHead>
                        <TableHead className="text-right">Nos</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detailOrder.items || []).map((it, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{it.product_name}</TableCell>
                          <TableCell className="font-mono text-xs">{it.sku_code}</TableCell>
                          <TableCell className="text-right">{it.qty_boxes_ordered}</TableCell>
                          <TableCell className="text-right">{it.qty_pcs_ordered || 0}</TableCell>
                          <TableCell className="text-right font-medium">{inr(it.line_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOrder(null)}>Close</Button>
            {detailOrder?.status === "pending" && (
              <Button className="bg-rose-700 hover:bg-rose-800 text-white" onClick={() => { setCancelFor(detailOrder); setDetailOrder(null); }} data-testid="sp-detail-cancel-order">
                <Ban size={14} className="mr-1" /> Cancel Order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoBlock({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

// ============================================================================
// SP: Receive Cash Payment (Phase 1)
// ============================================================================
// Shrinks phone photos before upload (keeps proof small, fast on mobile data)
const compressProofImage = (file, maxDim = 1280, quality = 0.72) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(img, 0, 0, w, h);
    c.toBlob((b) => {
      URL.revokeObjectURL(url);
      resolve(b ? new File([b], "payment-proof.jpg", { type: "image/jpeg" }) : file);
    }, "image/jpeg", quality);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export function SpCollectPaymentPage() {
  const [retailers, setRetailers] = useState([]);
  const [form, setForm] = useState({ retailer_id: "", amount: "", method: "cash", txn_ref: "", cheque_no: "", cheque_date: "", bank_name: "", description: "" });
  const [alloc, setAlloc] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [proof, setProof] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const nav = useNavigate();

  useEffect(() => { dms.listRetailers({ lite: 1 }).then(d => setRetailers(d.data || [])); }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return retailers;
    return retailers.filter(r => (r.name || "").toLowerCase().includes(n) || (r.phone || "").toLowerCase().includes(n));
  }, [retailers, q]);

  const selected = retailers.find(r => r.id === form.retailer_id);

  const pickProof = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("image/")) { toast.error("Please choose an image"); return; }
    const small = await compressProofImage(f);
    if (proofUrl) URL.revokeObjectURL(proofUrl);
    setProof(small);
    setProofUrl(URL.createObjectURL(small));
  };
  const clearProof = () => { if (proofUrl) URL.revokeObjectURL(proofUrl); setProof(null); setProofUrl(""); };

  const submit = async () => {
    setBusy(true);
    try {
      const amt = Number(form.amount);
      if (!form.retailer_id || !amt || amt <= 0) { toast.error("Pick retailer and enter amount"); setBusy(false); return; }
      if (form.method === "upi" && !form.txn_ref.trim()) { toast.error("Enter the UPI transaction / reference number"); setBusy(false); return; }
      if (form.method === "cheque" && !form.cheque_no.trim()) { toast.error("Enter the cheque number"); setBusy(false); return; }
      const res = await dms.recordSecondaryPayment({
        retailer_id: form.retailer_id,
        amount: amt,
        method: form.method,
        txn_ref: form.txn_ref || undefined,
        cheque_no: form.cheque_no || undefined,
        cheque_date: form.cheque_date || undefined,
        bank_name: form.bank_name || undefined,
        notes: form.description || undefined,
        description: form.description || `${form.method.toUpperCase()} collection`,
        allocations: alloc.length ? alloc : undefined,
      });
      if (proof && res && res.id) {
        try { await dms.uploadPaymentProof(res.id, proof); }
        catch (e2) { toast.warning("Payment saved, but the proof photo could not be uploaded"); }
      }
      toast.success(`Collected ${inr(amt)} (${form.method.toUpperCase()}) from ${selected?.name || "retailer"}`);
      setForm({ retailer_id: "", amount: "", method: "cash", txn_ref: "", cheque_no: "", cheque_date: "", bank_name: "", description: "" });
      clearProof();
      nav("/dms/salesperson");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const MODES = [
    { k: "cash", label: "Cash" },
    { k: "upi", label: "UPI" },
    { k: "cheque", label: "Cheque" },
  ];

  return (
    <div className="max-w-2xl mx-auto pb-24">
      <PageHeader title="Receive Payment" subtitle="Record cash / UPI / cheque collected from a retailer" back="/dms/salesperson" />
      <Card className="p-5 sm:p-6 space-y-5">
        <div>
          <Label>Retailer *</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search retailer…" className="pl-9" data-testid="sp-collect-search" />
          </div>
          <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
            {filtered.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">No retailers</div>}
            {filtered.map(r => (
              <button key={r.id}
                onClick={() => setForm({ ...form, retailer_id: r.id })}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${form.retailer_id === r.id ? "bg-[#faf6e6] border-l-4 border-[#a67c00]" : ""}`}
                data-testid={`sp-collect-pick-${r.id}`}>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-slate-500">{r.phone} · {r.address}</div>
              </button>
            ))}
          </div>
          {selected && <div className="mt-2 text-xs font-medium text-[#8a6600]">Selected: {selected.name}</div>}
        </div>

        <div>
          <Label>Payment Mode *</Label>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {MODES.map(m => (
              <button key={m.k} type="button"
                onClick={() => setForm({ ...form, method: m.k })}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${form.method === m.k ? "bg-[#a67c00] text-white border-[#a67c00]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`sp-collect-mode-${m.k}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Amount (₹) *</Label>
          <div className="relative mt-1">
            <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input type="number" inputMode="decimal" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="pl-8" data-testid="sp-collect-amount" />
          </div>
          <div className="mt-2">
            <LinkInvoiceButton
              className="h-10 w-full justify-center gap-2 text-sm border-dashed border-[#a67c00] text-[#8a6600]"
              retailerId={form.retailer_id} amount={form.amount}
              setAmount={(v) => setForm(f => ({ ...f, amount: String(v) }))}
              value={alloc} onChange={setAlloc} />
          </div>
        </div>

        {form.method === "upi" && (
          <div>
            <Label>UPI Txn / Ref # *</Label>
            <Input className="mt-1" value={form.txn_ref} onChange={e => setForm({ ...form, txn_ref: e.target.value })} placeholder="e.g. UPI ref no." data-testid="sp-collect-txnref" />
          </div>
        )}

        {form.method === "cheque" && (
          <>
            <div>
              <Label>Cheque # *</Label>
              <Input className="mt-1" value={form.cheque_no} onChange={e => setForm({ ...form, cheque_no: e.target.value })} placeholder="Cheque number" data-testid="sp-collect-chequeno" />
            </div>
            <div>
              <Label>Cheque Date</Label>
              <Input type="date" className="mt-1 w-full min-w-0" value={form.cheque_date} onChange={e => setForm({ ...form, cheque_date: e.target.value })} data-testid="sp-collect-chequedate" />
              {form.cheque_date && (
                <div className="mt-1 text-xs text-slate-500">
                  {new Date(form.cheque_date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input className="mt-1" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. HDFC" data-testid="sp-collect-bank" />
            </div>
          </>
        )}

        <div>
          <Label>Payment Proof <span className="text-slate-400 font-normal">(optional)</span></Label>
          <input id="sp-proof-cam" type="file" accept="image/*" capture="environment" className="hidden" onChange={pickProof} />
          <input id="sp-proof-gal" type="file" accept="image/*" className="hidden" onChange={pickProof} />
          {!proofUrl ? (
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => document.getElementById("sp-proof-cam").click()}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-[#a67c00] text-[#8a6600] text-sm font-medium hover:bg-[#faf6e6]"
                data-testid="sp-collect-proof-cam">
                <CamIcon size={16} /> Take Photo
              </button>
              <button type="button" onClick={() => document.getElementById("sp-proof-gal").click()}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
                data-testid="sp-collect-proof-gal">
                <ImgIcon size={16} /> Gallery
              </button>
            </div>
          ) : (
            <div className="mt-2 relative inline-block">
              <img src={proofUrl} alt="Payment proof" className="h-32 rounded-lg border border-slate-200 object-cover" />
              <button type="button" onClick={clearProof} aria-label="Remove photo"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow">
                <XIcon size={14} />
              </button>
            </div>
          )}
        </div>

        <div>
          <Label>Note</Label>
          <Textarea className="mt-1" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
        </div>
        <Button onClick={submit} disabled={busy || !form.retailer_id || !form.amount} className="w-full bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="sp-collect-submit">
          <Wallet size={16} className="mr-2" /> {busy ? "Saving…" : "Record Payment"}
        </Button>
      </Card>
    </div>
  );
}

// ============================================================================
// Team Leader Dashboard + Assignments
// ============================================================================
export function TeamLeaderDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const nav = useNavigate();
  useEffect(() => { dms.teamLeaderDashboard().then(d => setKpis(d.kpis)); }, []);
  return (
    <div>
      <PageHeader title="Team Leader Dashboard" subtitle="Manage distributors & sales team" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        {[
          { label: "My Distributors", value: kpis?.distributors ?? "—" },
          { label: "Salespersons", value: kpis?.salespersons ?? "—" },
          { label: "Sales MTD (incl. GST)", value: kpis ? inr(kpis.sales_mtd) : "—" },
        ].map(c => <Card key={c.label} className="p-4"><div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div><div className="mt-1 text-xl font-bold">{c.value}</div></Card>)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/team-leader/distributors")}><Handshake size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">My Distributors</div><div className="text-xs text-slate-500">View performance</div></Card>
        <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => nav("/dms/team-leader/assignments")}><Users size={20} className="text-[#a67c00]" /><div className="mt-2 font-semibold">Assign Distributors to Salesperson</div><div className="text-xs text-slate-500">Manage your sales team's coverage</div></Card>
      </div>
    </div>
  );
}

export function TlDistributorsPage() {
  const [list, setList] = useState([]);
  useEffect(() => { dms.listDistributors().then(d => setList(d.data)); }, []);
  return (
    <div>
      <PageHeader title="My Distributors" subtitle="Distributors assigned to your team" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(d => (
          <Card key={d.id} className="p-4"><div className="font-semibold">{d.name}</div><div className="text-xs text-slate-500 mt-0.5">{d.email}</div><div className="text-xs text-slate-500">Region: {d.region || "—"}</div></Card>
        ))}
      </div>
    </div>
  );
}

export function TlAssignmentsPage() {
  const [dists, setDists] = useState([]);
  const [sps, setSps] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const load = async () => {
    const [d, s, a] = await Promise.all([dms.listDistributors(), dms.listUsers("salesperson"), dms.listSpDistributors()]);
    setDists(d.data); setSps(s.data); setAssigns(a.data);
  };
  useEffect(() => { load(); }, []);
  const assign = async (spId, dId) => { await dms.assignSpDist({ salesperson_id: spId, distributor_id: dId }); toast.success("Assigned"); load(); };
  const unassign = async (spId, dId) => { await dms.unassignSpDist(spId, dId); toast.success("Removed"); load(); };
  const isAssigned = (spId, dId) => assigns.some(a => a.salesperson_id === spId && a.distributor_id === dId);
  return (
    <div>
      <PageHeader title="Assign Distributors to Salespersons" subtitle="Grant salespersons access to specific distributors" />
      <Card>
        <Table><TableHeader><TableRow><TableHead>Salesperson</TableHead>{dists.map(d => <TableHead key={d.id}>{d.name}</TableHead>)}</TableRow></TableHeader><TableBody>
          {sps.map(sp => (
            <TableRow key={sp.id}>
              <TableCell className="font-medium">{sp.name}<div className="text-xs text-slate-500">{sp.email}</div></TableCell>
              {dists.map(d => {
                const a = isAssigned(sp.id, d.id);
                return (
                  <TableCell key={d.id}>
                    <button onClick={() => a ? unassign(sp.id, d.id) : assign(sp.id, d.id)} className={`px-3 py-1 rounded text-xs font-medium ${a ? "bg-[#faf0cf] text-[#8a6600] hover:bg-[#faf0cf]" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`assign-${sp.id}-${d.id}`}>{a ? "✓ Assigned" : "Assign"}</button>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody></Table>
        {sps.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No salespersons found</div>}
      </Card>
    </div>
  );
}

// ============================================================================
// Owner: Assign Salespersons to Distributors (matrix) — assignment right lives
// with the Owner (removed from Team Leader per requirement).
// ============================================================================
export function OwnerSalesAssignmentsPage() {
  const [dists, setDists] = useState([]);
  const [sps, setSps] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const [d, s, a] = await Promise.all([dms.listDistributors(), dms.listUsers("salesperson"), dms.listSpDistributors()]);
      setDists(d.data || []); setSps(s.data || []); setAssigns(a.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const assign = async (spId, dId) => { try { await dms.assignSpDist({ salesperson_id: spId, distributor_id: dId }); toast.success("Assigned"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const unassign = async (spId, dId) => { try { await dms.unassignSpDist(spId, dId); toast.success("Removed"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const isAssigned = (spId, dId) => assigns.some(a => a.salesperson_id === spId && a.distributor_id === dId);
  return (
    <div>
      <PageHeader title="Assign Salespersons to Distributors" subtitle="Grant each salesperson access to specific distributors"
        action={<Button variant="outline" size="sm" onClick={load} disabled={busy}><RefreshCw size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh</Button>} />
      {/* Mobile-friendly: card list on small screens, matrix on large */}
      <div className="lg:hidden space-y-3">
        {sps.length === 0 && <Card className="p-8 text-center text-sm text-slate-500">No salespersons found</Card>}
        {sps.map(sp => (
          <Card key={sp.id} className="p-4">
            <div className="font-medium text-slate-900">{sp.name}</div>
            <div className="text-xs text-slate-500 mb-3">{sp.email}</div>
            <div className="flex flex-wrap gap-2">
              {dists.map(d => {
                const a = isAssigned(sp.id, d.id);
                return (
                  <button key={d.id} onClick={() => a ? unassign(sp.id, d.id) : assign(sp.id, d.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${a ? "bg-[#faf0cf] text-[#8a6600]" : "bg-slate-100 text-slate-600"}`}
                    data-testid={`owner-assign-${sp.id}-${d.id}`}>
                    {a ? "✓ " : ""}{d.name}
                  </button>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
      <Card className="hidden lg:block overflow-x-auto">
        <Table><TableHeader><TableRow><TableHead>Salesperson</TableHead>{dists.map(d => <TableHead key={d.id}>{d.name}</TableHead>)}</TableRow></TableHeader><TableBody>
          {sps.map(sp => (
            <TableRow key={sp.id}>
              <TableCell className="font-medium">{sp.name}<div className="text-xs text-slate-500">{sp.email}</div></TableCell>
              {dists.map(d => {
                const a = isAssigned(sp.id, d.id);
                return (
                  <TableCell key={d.id}>
                    <button onClick={() => a ? unassign(sp.id, d.id) : assign(sp.id, d.id)} className={`px-3 py-1 rounded text-xs font-medium ${a ? "bg-[#faf0cf] text-[#8a6600] hover:bg-[#faf0cf]" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`owner-assign-mx-${sp.id}-${d.id}`}>{a ? "✓ Assigned" : "Assign"}</button>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody></Table>
        {sps.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No salespersons found</div>}
      </Card>
    </div>
  );
}


export function RegionalManagerDashboardPage() {
  const [kpis, setKpis] = useState(null);
  useEffect(() => { dms.regionalManagerDashboard().then(d => setKpis(d.kpis)); }, []);
  return (
    <div>
      <PageHeader title="Regional Manager Dashboard" subtitle="Region-wide performance" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Team Leaders", value: kpis?.team_leaders ?? "—" },
          { label: "Distributors", value: kpis?.distributors ?? "—" },
          { label: "Sales MTD (incl. GST)", value: kpis ? inr(kpis.sales_mtd) : "—" },
        ].map(c => <Card key={c.label} className="p-4"><div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div><div className="mt-1 text-xl font-bold">{c.value}</div></Card>)}
      </div>
    </div>
  );
}


// ============================================================================
// SP: Today's Collection — date range + payment mode filter
// ============================================================================
export function SpCollectionsPage() {
  const { user } = useAuth();
  const isTl = user?.role === "team_leader";
  const isRm = user?.role === "regional_manager";
  const isDist = user?.role === "distributor" || user?.role === "distributor_accountant";
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [mode, setMode] = useState("all");
  const [salespersonId, setSalespersonId] = useState("");
  const [teamLeaderId, setTeamLeaderId] = useState("");
  const [salespersons, setSalespersons] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isTl) dms.tlSalespersons().then(d => setSalespersons(d.data || [])).catch(() => setSalespersons([]));
    if (isRm) {
      dms.rmTeamLeaders().then(d => setTeamLeaders(d.data || [])).catch(() => setTeamLeaders([]));
      dms.rmSalespersons().then(d => setSalespersons(d.data || [])).catch(() => setSalespersons([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo, mode };
    if (isTl && salespersonId) params.salesperson_id = salespersonId;
    if (isRm) {
      if (teamLeaderId) params.team_leader_id = teamLeaderId;
      if (salespersonId) params.salesperson_id = salespersonId;
    }
    dms.salespersonCollections(params)
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [dateFrom, dateTo, mode, salespersonId, teamLeaderId]);

  const modeLabel = { all: "All Modes", cash: "Cash", upi: "UPI", cheque: "Cheque", bank_transfer: "Bank Transfer", neft: "NEFT", rtgs: "RTGS", card: "Card" };
  const backTo = isTl ? "/dms/team-leader" : isRm ? "/dms/regional-manager" : isDist ? "/dms/distributor" : "/dms/salesperson";

  return (
    <div>
      <PageHeader title="Today's Collection" subtitle="Payments you have collected from retailers" back={backTo} />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Payment Mode</Label>
          <select value={mode} onChange={e => setMode(e.target.value)}
                  className="mt-1 flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm">
            {Object.entries(modeLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {isTl && (
          <div>
            <Label className="text-xs">Salesperson</Label>
            <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)}
                    className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
              <option value="">All Salespersons</option>
              {salespersons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        {isRm && (
          <>
            <div>
              <Label className="text-xs">Team Leader</Label>
              <select value={teamLeaderId} onChange={e => setTeamLeaderId(e.target.value)}
                      className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
                <option value="">All Team Leaders</option>
                {teamLeaders.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Salesperson</Label>
              <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)}
                      className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
                <option value="">All Salespersons</option>
                {salespersons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>

      <Card className="p-4 mb-4 bg-emerald-50 border-emerald-200">
        <div className="text-xs uppercase tracking-wider text-emerald-700">Total Collected</div>
        <div className="text-2xl font-bold text-emerald-900 mt-1">{inr(total)}</div>
        <div className="text-xs text-emerald-700 mt-0.5">{data.length} payment{data.length === 1 ? "" : "s"}</div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Party Name</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Date/Time</TableHead>
              <TableHead className="w-12">Proof</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((d, i) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{d.retailer_name}</TableCell>
                <TableCell>{inr(d.amount)}</TableCell>
                <TableCell className="capitalize">{modeLabel[d.method] || d.method}</TableCell>
                <TableCell className="text-xs text-slate-500">{d.reference_no || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(d.at)}</TableCell>
                <TableCell><ProofButton proofId={d.proof_id} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && !loading && <EmptyState message="No collections in this range" />}
      </Card>
    </div>
  );
}


// ============================================================================
// TL / RM: Today's Order Value — date range + salesperson (+ team leader for RM) filter
// ============================================================================
export function OrderValueDetailPage() {
  const { user } = useAuth();
  const isRm = user?.role === "regional_manager";
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [salespersonId, setSalespersonId] = useState("");
  const [teamLeaderId, setTeamLeaderId] = useState("");
  const [salespersons, setSalespersons] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isRm) {
      dms.rmTeamLeaders().then(d => setTeamLeaders(d.data || [])).catch(() => setTeamLeaders([]));
      dms.rmSalespersons().then(d => setSalespersons(d.data || [])).catch(() => setSalespersons([]));
    } else {
      dms.tlSalespersons().then(d => setSalespersons(d.data || [])).catch(() => setSalespersons([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (salespersonId) params.salesperson_id = salespersonId;
    if (isRm && teamLeaderId) params.team_leader_id = teamLeaderId;
    const call = isRm ? dms.rmOrders(params) : dms.tlOrders(params);
    call.then(d => setData(d.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [dateFrom, dateTo, salespersonId, teamLeaderId]);

  const activeOrders = data.filter(o => o.status !== "cancelled");
  const total = activeOrders.reduce((s, o) => s + (o.total || 0), 0);
  const backTo = isRm ? "/dms/regional-manager" : "/dms/team-leader";

  return (
    <div>
      <PageHeader title="Order Value" subtitle="Orders placed in this range — invoice status doesn't matter" back={backTo} />

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-1" />
        </div>
        {isRm && (
          <div>
            <Label className="text-xs">Team Leader</Label>
            <select value={teamLeaderId} onChange={e => setTeamLeaderId(e.target.value)}
                    className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
              <option value="">All Team Leaders</option>
              {teamLeaders.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label className="text-xs">Salesperson</Label>
          <select value={salespersonId} onChange={e => setSalespersonId(e.target.value)}
                  className="mt-1 flex h-9 w-[180px] rounded-md border border-input bg-background px-3 py-1 text-sm">
            <option value="">All Salespersons</option>
            {salespersons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>

      <Card className="p-4 mb-4 bg-sky-50 border-sky-200">
        <div className="text-xs uppercase tracking-wider text-sky-700">Total Order Value</div>
        <div className="text-2xl font-bold text-sky-900 mt-1">{inr(total)}</div>
        <div className="text-xs text-sky-700 mt-0.5">{activeOrders.length} order{activeOrders.length === 1 ? "" : "s"}{data.length !== activeOrders.length ? ` (${data.length - activeOrders.length} cancelled excluded)` : ""}</div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Retailer</TableHead>
              {isRm && <TableHead>Distributor</TableHead>}
              <TableHead>Salesperson</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Placed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map(o => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{o.order_no}</TableCell>
                <TableCell className="font-medium">{o.retailer_name}</TableCell>
                {isRm && <TableCell className="text-xs text-slate-500">{o.distributor_name}</TableCell>}
                <TableCell className="text-xs text-slate-500">{o.placed_by_name || "—"}</TableCell>
                <TableCell className="text-right font-semibold">{inr(o.total)}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPill(o.status)}`}>{(o.status || "").replace(/_/g, " ")}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && !loading && <EmptyState message="No orders in this range" />}
      </Card>
    </div>
  );
}


// ============================================================================
// SP: Edit Retailer — locked fields (name/phone/address) are read-only;
// region, credit limit, GSTIN, login, location & documents are editable.
// ============================================================================
export function SpEditRetailerPage() {
  const { retailerId } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dms.getRetailer(retailerId).then(r => {
      setForm({
        name: r.name || "", phone: r.phone || "", address: r.address || "",
        region: r.region || "", credit_limit: r.credit_limit || 0,
        email: r.email || "", password: "",
        gstin: r.kyc?.gstin || "", shop_license: r.kyc?.shop_license || "",
        gps_lat: r.gps_lat ?? "", gps_lng: r.gps_lng ?? "", location_link: r.location_link || "",
        documents: r.documents || [],
        retailer_type: r.retailer_type || "retailer",
      });
    }).catch(() => toast.error("Could not load retailer"));
  }, [retailerId]);

  const submit = async () => {
    if (!form) return;
    setBusy(true);
    try {
      const body = {
        region: form.region,
        credit_limit: Number(form.credit_limit) || 0,
        gps_lat: form.gps_lat === "" ? null : Number(form.gps_lat),
        gps_lng: form.gps_lng === "" ? null : Number(form.gps_lng),
        location_link: form.location_link,
        documents: form.documents,
        kyc: { gstin: form.gstin, shop_license: form.shop_license },
        retailer_type: form.retailer_type || "retailer",
      };
      if (form.email && form.email.trim()) body.email = form.email.trim();
      if (form.password && form.password.trim()) body.password = form.password.trim();
      await dms.updateRetailer(retailerId, body);
      toast.success("Retailer updated");
      nav("/dms/salesperson/retailers");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setBusy(false);
  };

  if (!form) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="Edit Retailer" subtitle="Shop name, phone & address are locked — everything else can be updated" back="/dms/salesperson/retailers" />
      <Card className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Shop Name <span className="text-xs text-slate-400">(locked)</span></Label><Input value={form.name} disabled className="bg-slate-50 text-slate-500" /></div>
          <div><Label>Phone <span className="text-xs text-slate-400">(locked)</span></Label><Input value={form.phone} disabled className="bg-slate-50 text-slate-500" /></div>
        </div>
        <div><Label>Address <span className="text-xs text-slate-400">(locked)</span></Label><Textarea rows={2} value={form.address} disabled className="bg-slate-50 text-slate-500" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Region</Label><Input value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} /></div>
          <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Login Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>New Password <span className="text-xs text-slate-400">(leave blank to keep)</span></Label><Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>GSTIN</Label><Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} /></div>
          <div><Label>Shop License</Label><Input value={form.shop_license} onChange={e => setForm({ ...form, shop_license: e.target.value })} /></div>
        </div>
        <div><Label>Retailer Type *</Label>
          <select className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200" value={form.retailer_type || "retailer"} onChange={e => setForm({ ...form, retailer_type: e.target.value })} data-testid="sp-ret-type">
            <option value="retailer">Retailer</option>
            <option value="wholeseller">Wholeseller</option>
            <option value="workshop">Workshop</option>
            <option value="mechanic">Mechanic</option>
          </select></div>
        <LocationDocumentsBlock
          lat={form.gps_lat}
          lng={form.gps_lng}
          locationLink={form.location_link}
          onLat={(v) => setForm(f => ({ ...f, gps_lat: v }))}
          onLng={(v) => setForm(f => ({ ...f, gps_lng: v }))}
          onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
          documents={form.documents}
          onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
          helpText="The GPS pin cannot be saved here. At the shop, open My Retailers and tap 'Save shop location'. You can add documents and photos here."
        />
        <Button onClick={submit} disabled={busy} className="w-full bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="sp-save-edit-retailer">
          {busy ? "Saving…" : "Save Changes"}
        </Button>
      </Card>
    </div>
  );
}

// ============================================================================
// SP: Outstanding — distributor-wise, drill into retailer-wise breakdown
// ============================================================================
export function SpOutstandingPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [distFor, setDistFor] = useState(null);
  const [retRows, setRetRows] = useState([]);
  const [retTotal, setRetTotal] = useState(0);
  const [retLoading, setRetLoading] = useState(false);

  useEffect(() => {
    dms.spOutstanding().then(d => { setRows(d.data || []); setTotal(d.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openDistributor = async (d) => {
    setDistFor(d);
    setRetLoading(true);
    try {
      const res = await dms.spOutstandingRetailers(d.distributor_id);
      setRetRows(res.data || []);
      setRetTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load retailers");
      setRetRows([]);
    }
    setRetLoading(false);
  };

  return (
    <div>
      <PageHeader title="Outstanding" subtitle="Distributor-wise retailer outstanding — tap a row to see retailer breakdown" back="/dms/salesperson" />
      <Card className="p-4 mb-4 bg-rose-50 border-rose-200">
        <div className="text-xs uppercase tracking-wider text-rose-700">Total Outstanding</div>
        <div className="text-2xl font-bold text-rose-900 mt-1">{inr(total)}</div>
        <div className="text-xs text-rose-700 mt-0.5">{rows.length} distributor{rows.length === 1 ? "" : "s"}</div>
      </Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Distributor</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={2} className="text-center py-8 text-slate-400">No outstanding — all clear</TableCell></TableRow>}
            {rows.map(d => (
              <TableRow key={d.distributor_id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDistributor(d)} data-testid={`sp-outstanding-row-${d.distributor_id}`}>
                <TableCell className="font-medium underline decoration-dotted">{d.distributor_name}</TableCell>
                <TableCell className="text-right font-semibold text-rose-700">{inr(d.outstanding)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!distFor} onOpenChange={o => { if (!o) { setDistFor(null); setRetRows([]); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Outstanding — {distFor?.distributor_name}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-2">Total: <span className="font-semibold text-rose-700">{inr(retTotal)}</span></div>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Retailer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {retLoading && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
                {!retLoading && retRows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-400">No outstanding retailers</TableCell></TableRow>}
                {retRows.map(r => (
                  <TableRow key={r.retailer_id}>
                    <TableCell className="font-medium">{r.retailer_name}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.phone || "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-rose-700">{inr(r.outstanding)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
